/*
 * DrivePage — the reference vault-app Drive experience (Drive.jsx + FolderView.jsx +
 * SimplePages.jsx + SharedWithMe.jsx), wired to the real Vault API. Reference
 * DOM/classes/copy are kept 1:1; only the DATA (demo refData → vaultApi) and the
 * control handlers changed.
 *
 * The active view is derived from the URL "tab" query (react-router):
 *   (none) → Drive   ·   shared → SharedWithMe   ·   recent → Recent
 *   starred → Starred   ·   deleted → Trash
 * Folder drilldown is a real folder stack over vaultApi.listChildren.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { cardStagger } from "@/shared/motion/transitions";
import { promptDialog, confirmDialog } from "@/shared/ui";
import { useApp } from "../vault/ctx";
import { FileCard } from "../vault/Common";
import Ic from "../vault/Ic";
import PosterWorker from "./PosterWorker";
import {
  listChildren,
  listModels,
  restoreModel,
  purgeModel,
  renameFolder,
  deleteFolder,
  type VaultFolder,
  type VaultModel,
} from "@/features/vault/vaultApi";
import {
  listSharedWithMe,
  dismissShare,
  type InboundShare,
  type ShareAccess,
} from "@/features/vault/sharedApi";

// ── Display helpers (real model → reference row bits) ────────────────────────
const FMT_COLOR: Record<string, string> = {
  glb: "#33608F", gltf: "#33608F", "3dm": "#7A5C2E",
  stl: "#5B5F66", obj: "#5B5F66", fbx: "#5B5F66", ply: "#5B5F66",
  dwg: "#5B5F66", stp: "#5B5F66", step: "#5B5F66",
  png: "#2E7D4F", jpg: "#2E7D4F", jpeg: "#2E7D4F", webp: "#2E7D4F",
  mp4: "#8F4E8B", webm: "#8F4E8B", gif: "#8F4E8B",
};
const icoColor = (fmt = "") => FMT_COLOR[fmt.toLowerCase()] ?? "#7A5C2E";
const fmtIc = (fmt = "") => fmt.toUpperCase().slice(0, 4) || "3D";
const fmtDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
function fmtSize(bytes?: number): string {
  if (!bytes) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let v = bytes, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

const CHIPS: [string, string][] = [
  ["all", "All"], ["models", "3D models"], ["renders", "Renders"],
  ["cad", "CAD files"], ["stones", "Stones"], ["docs", "Docs"],
];
const MODEL_FMT = ["glb", "gltf", "obj", "fbx", "stl", "3dm", "ply", "usdz"];
const RENDER_FMT = ["png", "jpg", "jpeg", "webp", "mp4", "webm", "gif"];
const CAD_FMT = ["dwg", "stp", "step", "3dm", "igs", "iges"];
function matchesChip(m: VaultModel, chip: string): boolean {
  const fmt = (m.format || "").toLowerCase();
  const txt = `${m.category ?? ""} ${m.tags.join(" ")} ${m.name}`.toLowerCase();
  switch (chip) {
    case "models": return MODEL_FMT.includes(fmt);
    case "renders": return RENDER_FMT.includes(fmt);
    case "cad": return CAD_FMT.includes(fmt);
    case "stones": return /stone|gem|diamond|emerald|ruby|sapphire/.test(txt);
    case "docs": return ["pdf", "dwg", "doc", "docx", "txt"].includes(fmt);
    default: return true;
  }
}

type SortKey = "modified" | "name";
const sortModels = (ms: VaultModel[], sort: SortKey) =>
  [...ms].sort((a, b) =>
    sort === "name" ? a.name.localeCompare(b.name) : +new Date(b.updatedAt) - +new Date(a.updatedAt));

// Cosmetic folder chips (icon + colour) cycled by position — meta is the real childCount.
const FOLDER_PALETTE = [
  { icon: "ring", c: "#A0793D", bg: "var(--accent-soft)" },
  { icon: "necklace", c: "#5B6C9E", bg: "rgba(96,118,205,.14)" },
  { icon: "earrings", c: "#2E7D4F", bg: "var(--green-soft)" },
  { icon: "bangle", c: "#966E48", bg: "rgba(150,110,72,.12)" },
  { icon: "gem", c: "#C0442E", bg: "var(--red-soft)" },
  { icon: "folder", c: "#8B8578", bg: "var(--surface-2)" },
];

// ── Folder kebab: real Rename / Delete (renameFolder/deleteFolder), inline menu
// mirroring CollectionsPage. The shared "ctx" modal only acts on models, so folders
// get their own menu instead of opening a stale model context. ──────────────
function FolderKebab({ folder, onChanged }: { folder: VaultFolder; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const rename = async () => {
    setOpen(false);
    const name = await promptDialog({ title: "Rename folder", label: "Name", defaultValue: folder.name });
    if (!name || !name.trim() || name.trim() === folder.name) return;
    try { await renameFolder(folder._id, name.trim()); onChanged(); } catch { /* no-op */ }
  };
  const remove = async () => {
    setOpen(false);
    if (!(await confirmDialog({ title: `Delete “${folder.name}”?`, body: "Files inside move to Trash.", danger: true, confirmLabel: "Delete" }))) return;
    try { await deleteFolder(folder._id); onChanged(); } catch { /* no-op */ }
  };
  return (
    <>
      <button className="kebab" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>⋮</button>
      {open && (
        <div onClick={(e) => e.stopPropagation()} style={{ position: "absolute", top: 34, right: 8, zIndex: 5, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--shadow-lg)", padding: 6, minWidth: 140 }}>
          <button className="nav-item" style={{ width: "100%" }} onClick={rename}>Rename…</button>
          <button className="nav-item" style={{ width: "100%", color: "var(--red)" }} onClick={remove}>Delete</button>
        </div>
      )}
    </>
  );
}

// ── Regenerate thumbnails ────────────────────────────────────────────────────
/*
 * Studio only auto-captures a poster for a model that has NONE, so a thumbnail
 * taken before a rendering fix (e.g. the tone-mapped grey backdrop) would stay
 * wrong forever — the only cure was opening every model in Studio by hand and
 * pressing "Update thumbnail". This is the explicit way out: it mounts
 * PosterWorker, the offscreen drive-wide thumbnailer, over the models on screen
 * (or just the selected ones) and re-renders each against the viewer's current
 * default scene.
 *
 * There is no staleness heuristic on purpose: nothing records WHEN a poster was
 * captured or with which renderer, so any "looks old" rule would be a guess.
 * The user picks.
 */
interface PosterRun {
  models: VaultModel[];
  done: number;
  failed: number;
  /** The worker drained (or the user stopped it) — keep the tally, drop the viewer. */
  finished: boolean;
}

function RegenerateThumbnails({
  models,
  onPoster,
}: {
  models: VaultModel[];
  onPoster: (id: string, posterUrl: string) => void;
}) {
  const { selected } = useApp();
  const [run, setRun] = useState<PosterRun | null>(null);

  // Only "ready" models have a GLB to render; anything still converting (or
  // errored) would just fail its turn in the queue.
  const targets = (selected.length ? models.filter((m) => selected.includes(m._id)) : models)
    .filter((m) => m.status === "ready");
  const running = !!run && !run.finished;

  const start = async () => {
    if (running || targets.length === 0) return;
    const n = targets.length;
    const ok = await confirmDialog({
      title: n === 1 ? "Regenerate this thumbnail?" : `Regenerate ${n} thumbnails?`,
      body: `Each model is re-rendered in a hidden viewer — roughly ${n * 4}s in total. Keep this tab open until it finishes.`,
      confirmLabel: "Regenerate",
    });
    if (!ok) return;
    setRun({ models: targets, done: 0, failed: 0, finished: false });
  };

  const onResult = useCallback(
    (id: string, posterUrl: string | null) => {
      if (posterUrl) onPoster(id, posterUrl);
      setRun((r) => (r ? { ...r, done: r.done + 1, failed: r.failed + (posterUrl ? 0 : 1) } : r));
    },
    [onPoster],
  );
  const stop = useCallback(() => setRun((r) => (r ? { ...r, finished: true } : r)), []);

  // Clear the tally a few seconds after a finished run so the toolbar goes back
  // to being a toolbar.
  useEffect(() => {
    if (!run?.finished) return;
    const t = setTimeout(() => setRun(null), 4000);
    return () => clearTimeout(t);
  }, [run?.finished]);

  const label = selected.length ? `Regenerate ${targets.length} thumbnails` : "Regenerate thumbnails";

  return (
    <>
      {running ? (
        <>
          <span style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>
            Rendering {Math.min(run.done + 1, run.models.length)} / {run.models.length}…
          </span>
          <button className="btn ghost" style={{ padding: "7px 14px", fontSize: 12.5 }} onClick={stop}>Stop</button>
        </>
      ) : run ? (
        <span style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>
          {run.done - run.failed} thumbnail{run.done - run.failed === 1 ? "" : "s"} updated
          {run.failed ? ` · ${run.failed} failed` : ""}
        </span>
      ) : (
        <button
          className="btn ghost"
          style={{ padding: "7px 14px", fontSize: 12.5 }}
          disabled={targets.length === 0}
          title="Re-render the thumbnails for these models"
          onClick={() => void start()}
        >
          <Ic n="image" size={13} /> {label}
        </button>
      )}
      {/* Empty queue → PosterWorker renders nothing, which disposes its viewer. */}
      <PosterWorker pending={running ? run.models : []} onResult={onResult} onDone={stop} />
    </>
  );
}

// ── Root: pick the view from the URL "tab" query ────────────────────────────
export default function DrivePage() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  if (tab === "shared") return <SharedWithMe />;
  if (tab === "recent") return <Recent />;
  if (tab === "starred") return <Starred />;
  if (tab === "deleted") return <Trash />;
  return <Drive />;
}

// ── Drive + folder drilldown (Drive.jsx + FolderView.jsx) ────────────────────
function Drive() {
  const { open, go, setBulkCount, selected, setModalData, driveRev } = useApp();
  const [chip, setChip] = useState("all");
  const [view, setView] = useState("grid");
  const [sort, setSort] = useState<SortKey>("modified");
  const [checklist, setChecklist] = useState(true);
  const [path, setPath] = useState<VaultFolder[]>([]);
  const [data, setData] = useState<{ folders: VaultFolder[]; models: VaultModel[] } | null>(null);

  const parentId = path.length ? path[path.length - 1]._id : null;
  // Request-id guard: a slow listChildren for a previous folder must not overwrite
  // the current folder's contents when navigating quickly (out-of-order responses).
  const reqRef = useRef(0);
  const load = useCallback(() => {
    const req = ++reqRef.current;
    setData(null);
    listChildren(parentId)
      .then((d) => { if (reqRef.current === req) setData(d); })
      .catch(() => { if (reqRef.current === req) setData({ folders: [], models: [] }); });
    // driveRev is a refresh signal from the global modals (see VaultCtx.bumpDrive);
    // it takes part in no request, it only re-runs this loader.
  }, [parentId, driveRev]);
  useEffect(() => { load(); }, [load]);

  // Patch the one card in place instead of re-running `load()`: a full refetch
  // blanks the grid to "Loading…" after every single regenerated thumbnail.
  const patchPoster = useCallback((id: string, posterUrl: string) => {
    setData((d) => (d ? { ...d, models: d.models.map((m) => (m._id === id ? { ...m, posterUrl } : m)) } : d));
  }, []);

  const models = data ? sortModels(data.models, sort) : [];

  // Share modal is per-model and modalData is sticky; opening it with no file
  // target (folder share / checklist) must clear the last previewed/shared model
  // or the modal shows stale data. (Mirrors LinksPage's openShare clearing.)
  const openBlankShare = () => { setModalData("share", null); setModalData("preview", null); open("share"); };

  // ── Folder view (inside a folder) ──────────────────────────────────────────
  if (path.length > 0) {
    return (
      <>
        <div className="crumbs">
          <button className="crumb" onClick={() => setPath([])}>My Drive</button>
          {path.map((fo, i) => (
            <span key={fo._id}>
              {" › "}
              {i === path.length - 1
                ? <span className="here">{fo.name}</span>
                : <button className="crumb" onClick={() => setPath(path.slice(0, i + 1))}>{fo.name}</button>}
            </span>
          ))}
          <span style={{ flex: 1 }}></span>
          <RegenerateThumbnails models={models} onPoster={patchPoster} />
          <button className="btn ghost" onClick={() => { setBulkCount(selected.length || models.length); open("bulk"); }}><Ic n="bolt" size={13} /> Bulk render</button>
          <button className="btn ghost" onClick={() => go("render")}><Ic n="sliders" size={13} /> Folder render settings</button>
          <button className="btn gold" onClick={openBlankShare}><Ic n="link" size={13} /> Share folder</button>
        </div>

        {data === null ? (
          <div className="pg-empty" style={{ marginTop: 12 }}>Loading…</div>
        ) : (
          <>
            {data.folders.length > 0 && (
              <>
                <div className="sec-head">Subfolders</div>
                <div className="folder-grid">
                  {data.folders.map((fo) => (
                    <div key={fo._id} className="folder-card" onClick={() => setPath([...path, fo])}>
                      <div className="folder-ico" style={{ background: "var(--accent-soft)", color: "#A0793D" }}><Ic n="folder" size={19} sw={1.7} /></div>
                      <div><div className="f-name">{fo.name}</div><div className="f-meta">{fo.childCount ?? 0} items</div></div>
                      <FolderKebab folder={fo} onChanged={load} />
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="sec-head">Files</div>
            {models.length ? (
              <motion.div className="file-grid" variants={cardStagger} initial="hidden" animate="show">{models.map((m, i) => <FileCard key={m._id} model={m} index={i} />)}</motion.div>
            ) : (
              <div className="pg-empty">This folder is empty.</div>
            )}
          </>
        )}
      </>
    );
  }

  // ── Drive root ─────────────────────────────────────────────────────────────
  const files = models.filter((m) => matchesChip(m, chip));

  return (
    <>
      <div className="crumbs"><span className="here">My Drive</span></div>

      {checklist && (
        <div className="checklist">
          <div className="cl-ring"><i>3/5</i></div>
          <div style={{ minWidth: 150 }}><b style={{ fontSize: 13 }}>Make your vault shine</b><div className="hint">2 steps to a client-ready setup</div></div>
          <div className="cl-items">
            <span className="cl-it done">✓ Connect your S3</span>
            <span className="cl-it done">✓ Upload your logo</span>
            <span className="cl-it done">✓ Set global render settings</span>
            <span className="cl-it">○ <a href="#" onClick={e => { e.preventDefault(); openBlankShare(); }} style={{ color: "var(--accent-strong)" }}>Create your first share link</a></span>
            <span className="cl-it">○ <a href="#" onClick={e => e.preventDefault()} style={{ color: "var(--accent-strong)" }}>Invite your team</a></span>
          </div>
          <button className="m-close" onClick={() => setChecklist(false)} title="Dismiss">✕</button>
        </div>
      )}

      <div className="toolbar">
        {CHIPS.map(([k, l]) => (
          <button key={k} className={"chip" + (chip === k ? " on" : "")} onClick={() => setChip(k)}>{l}</button>
        ))}
        <div className="grow"></div>
        <RegenerateThumbnails models={files} onPoster={patchPoster} />
        <button className="btn ghost" style={{ padding: "7px 14px", fontSize: 12.5 }} onClick={() => { setBulkCount(selected.length); open("bulk"); }}><Ic n="bolt" size={13} /> Bulk render</button>
        <button className="sort-btn" onClick={() => setSort((s) => (s === "modified" ? "name" : "modified"))}><Ic n="sort" size={14} sw={2} /> {sort === "modified" ? "Modified" : "Name"}</button>
        <div className="seg">
          <button className={view === "grid" ? "on" : ""} title="Grid" onClick={() => setView("grid")}><Ic n="grid" size={14} sw={2} /></button>
          <button className={view === "list" ? "on" : ""} title="List" onClick={() => setView("list")}><Ic n="menu" size={14} sw={2} /></button>
        </div>
      </div>

      {data === null ? (
        <div className="pg-empty" style={{ marginTop: 12 }}>Loading your drive…</div>
      ) : (
        <>
          {data.folders.length > 0 && (
            <>
              <div className="sec-head">Folders <span className="n">· {data.folders.length}</span></div>
              <div className="folder-grid">
                {data.folders.map((fo, i) => {
                  const p = FOLDER_PALETTE[i % FOLDER_PALETTE.length];
                  return (
                    <div key={fo._id} className="folder-card" onClick={() => setPath([fo])}>
                      <div className="folder-ico" style={{ background: p.bg, color: p.c }}><Ic n={p.icon} size={20} sw={1.7} /></div>
                      <div><div className="f-name">{fo.name}</div><div className="f-meta">{fo.childCount ?? 0} items</div></div>
                      <FolderKebab folder={fo} onChanged={load} />
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="sec-head">Files <span className="n">· {files.length} shown</span></div>
          {files.length === 0 ? (
            // Empty state must show in BOTH views — the list branch used to render a
            // bare header-only table with no message.
            <div className="pg-empty" style={{ marginTop: 12 }}>
              {data.models.length ? "Nothing matches this filter in the current folder." : "No files here yet — upload a model to get started."}
            </div>
          ) : view === "grid" ? (
            <motion.div className="file-grid" variants={cardStagger} initial="hidden" animate="show">
              {files.map((m, i) => <FileCard key={m._id} model={m} index={i} />)}
            </motion.div>
          ) : (
            <table className="list-table on">
              <thead><tr><th style={{ width: "44%" }}>Name</th><th>Type</th><th>Size</th><th>Modified</th></tr></thead>
              <tbody>
                {files.map((m) => (
                  <tr key={m._id} onClick={() => { setModalData("preview", m); open("preview"); }}>
                    <td className="nm"><span className="mini-ico" style={{ background: icoColor(m.format) }}>{fmtIc(m.format)}</span> {m.name}</td>
                    <td>{(m.format || "").toUpperCase()}</td><td>{fmtSize(m.glbSize || m.size)}</td><td>{fmtDate(m.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  );
}

// ── Simple model lists (Recent / Starred) ────────────────────────────────────
function useModelList(opts: Parameters<typeof listModels>[1]) {
  const [models, setModels] = useState<VaultModel[] | null>(null);
  const load = useCallback(() => {
    setModels(null);
    listModels(null, opts).then(setModels).catch(() => setModels([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(opts)]);
  useEffect(() => { load(); }, [load]);
  return { models, load };
}

function Recent() {
  const { models } = useModelList({ recent: true });
  return (<>
    <div className="page-title">Recent</div><div className="page-sub">Picking up where you left off.</div>
    <div className="sec-head">Today</div>
    {models === null ? <div className="pg-empty">Loading…</div>
      : models.length ? <motion.div className="file-grid" variants={cardStagger} initial="hidden" animate="show">{models.map((m, i) => <FileCard key={m._id} model={m} index={i} />)}</motion.div>
        : <div className="pg-empty">No recent files yet — open a model to see it here.</div>}
  </>);
}

function Starred() {
  const { models, load } = useModelList({ starred: true });
  return (<>
    <div className="page-title">Starred</div><div className="page-sub">Your pinned models and renders.</div>
    {models === null ? <div className="pg-empty" style={{ marginTop: 16 }}>Loading…</div>
      : models.length ? <motion.div className="file-grid" style={{ marginTop: 16 }} variants={cardStagger} initial="hidden" animate="show">{models.map((m, i) => <FileCard key={m._id} model={m} index={i} onChanged={load} />)}</motion.div>
        : <div className="pg-empty" style={{ marginTop: 16 }}>Nothing starred yet — hover a file and tap the star.</div>}
  </>);
}

// ── Trash (SimplePages.jsx) ──────────────────────────────────────────────────
type TrashModel = VaultModel & { deletedAt?: string | null };
/*
 * How long this item has been in the trash.
 *
 * This column used to count DOWN to an automatic 30-day deletion, and the page
 * subtitle promised one. No such job exists: the server has exactly one
 * scheduled task (the render-job reaper in modules/render/renderWorker.ts) and
 * no Mongo TTL index on `deletedAt`, so trashed models are retained until
 * someone presses "Delete forever". Showing a countdown that never fires is a
 * retention promise the product does not keep, so we report the real fact —
 * age — instead. If auto-purge is implemented later, restore the countdown.
 */
function deletedAge(m: TrashModel): string {
  const d = m.deletedAt;
  if (!d) return "—";
  const days = Math.floor((Date.now() - +new Date(d)) / 86_400_000);
  if (days <= 0) return "Today";
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

function Trash() {
  const { models, load } = useModelList({ trash: true });
  const [busy, setBusy] = useState<string | null>(null);

  const restore = (id: string) => { setBusy(id); restoreModel(id).then(load).finally(() => setBusy(null)); };
  const purge = async (id: string, name: string) => {
    if (!(await confirmDialog({ title: `Permanently delete “${name}” and its assets?`, body: "This cannot be undone.", danger: true, confirmLabel: "Delete" }))) return;
    setBusy(id); purgeModel(id).then(load).finally(() => setBusy(null));
  };

  return (<>
    <div className="page-title">Trash</div><div className="page-sub">Items stay here until you delete them permanently.</div>
    {models === null ? (
      <div className="pg-empty" style={{ marginTop: 16 }}>Loading…</div>
    ) : models.length === 0 ? (
      <div className="pg-empty" style={{ marginTop: 16 }}>Trash is empty.</div>
    ) : (
      <table className="list-table on" style={{ marginTop: 16 }}>
        <thead><tr><th style={{ width: "45%" }}>Name</th><th>Deleted</th><th>In trash</th><th></th></tr></thead>
        <tbody>
          {(models as TrashModel[]).map((m) => (
            <tr key={m._id}>
              <td className="nm"><span className="mini-ico" style={{ background: icoColor(m.format) }}>{fmtIc(m.format)}</span> {m.name}</td>
              <td>{fmtDate(m.deletedAt ?? undefined)}</td>
              <td>{deletedAge(m)}</td>
              <td style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <button className="btn ghost" style={{ padding: "5px 10px", fontSize: 12 }} disabled={busy === m._id} onClick={() => restore(m._id)}>Restore</button>
                <button className="btn danger" style={{ padding: "5px 10px", fontSize: 12 }} disabled={busy === m._id} onClick={() => purge(m._id, m.name)}>Delete forever</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </>);
}

// ── Shared with me (SharedWithMe.jsx) — real inbound shares ──────────────────
const ACCESS_LABEL: Record<ShareAccess, string> = {
  view: "Can view",
  download: "Can download",
  comment: "Can comment",
};

function SharedWithMe() {
  const { open, setModalData } = useApp();
  const [shares, setShares] = useState<InboundShare[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setShares(null);
    listSharedWithMe().then(setShares).catch(() => setShares([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // The shared model lives in another workspace, so seed the preview directly from
  // the populated grant (the modal's own getModel refetch is workspace-scoped and
  // 404s cross-tenant — it falls back to this seed).
  const openPreview = (s: InboundShare) => {
    const m = s.modelId;
    const seed: VaultModel = {
      _id: m._id, name: m.name, format: m.format,
      posterUrl: m.posterUrl ?? null, posterKey: m.posterKey,
      folderId: null, status: "ready", size: 0, tags: [], layers: [],
      visibility: "shared", createdAt: s.createdAt, updatedAt: s.updatedAt,
    };
    setModalData("preview", seed);
    open("preview");
  };

  const dismiss = (id: string) => { setBusy(id); dismissShare(id).then(load).finally(() => setBusy(null)); };

  return (
    <>
      <div className="page-title">Shared with me</div>
      <div className="page-sub">Models people have shared with you, across workspaces.</div>
      {shares === null ? (
        <div className="pg-empty" style={{ marginTop: 16 }}>Loading…</div>
      ) : shares.length === 0 ? (
        <div className="pg-empty" style={{ marginTop: 16 }}>Nothing has been shared with you yet.</div>
      ) : (
        <table className="list-table on" style={{ marginTop: 16 }}>
          <thead><tr><th style={{ width: "38%" }}>Name</th><th>Shared by</th><th>Access</th><th>Shared on</th><th></th></tr></thead>
          <tbody>
            {shares.map((s) => (
              <tr key={s._id} onClick={() => openPreview(s)}>
                <td className="nm"><span className="mini-ico" style={{ background: icoColor(s.modelId.format) }}>{fmtIc(s.modelId.format)}</span> {s.modelId.name}</td>
                <td>{s.sharedByName || s.ownerWorkspaceId?.name || "Someone"}</td>
                <td><span className="badge gold">{ACCESS_LABEL[s.access]}</span></td>
                <td>{fmtDate(s.createdAt)}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn ghost" style={{ padding: "5px 10px", fontSize: 12 }} disabled={busy === s._id} onClick={(e) => { e.stopPropagation(); dismiss(s._id); }}>Dismiss</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
